/**
 * Consumer-page registry.
 *
 * Pages listed here attract personal/consumer users (e.g. someone Googling
 * "locked out of my Microsoft account") rather than our B2B ICP. On these
 * pages we:
 *
 *   - suppress all conversion CTAs (ContextualServiceCTA, sticky mobile,
 *     floating help, exit intent, early-mobile contact banner)
 *   - load AdSense via <AdSenseLoader /> (Auto Ads on/off is a dashboard setting)
 *
 * Keys for KB articles use `{category}/{slug}` to avoid slug collisions
 * across categories. Tools and blog posts use their slug directly.
 */

import { getAlert24VariantForBlog, getAlert24VariantForKB } from './alert24-funnel';
import { isSoftwareQuoteFunnelKB } from './software-quote-funnel';
import { isGlitchReplayFunnelKB } from './glitchreplay-funnel';
import { getPlanetRoadmapVariantForBlog } from './planetroadmap-funnel';
import { getWideAreaAIVariantForBlog, getWideAreaAIVariantForKB } from './wideareaai-funnel';

export const CONSUMER_KB_ARTICLES = new Set<string>([
  // Microsoft account / login / recovery
  'microsoft/how-to-recover-microsoft-account',
  'microsoft/how-to-use-microsoft-account-recovery-form',
  'microsoft/how-to-delete-microsoft-account-permanently',
  'microsoft/how-to-remove-microsoft-account-windows-10',
  'microsoft/how-to-install-windows-11-without-microsoft-account',
  'microsoft/how-to-unlink-microsoft-account-ps5',
  'microsoft/how-to-set-up-microsoft-passkey',
  'microsoft/how-to-transfer-microsoft-authenticator-new-phone',
  'microsoft/how-to-fix-microsoft-authenticator-not-working',
  'microsoft/how-to-use-microsoft-edge-password-manager',

  // Microsoft scam / phishing awareness
  'microsoft/how-to-identify-microsoft-security-alert-scams',
  'microsoft/how-to-verify-microsoft-single-use-code-emails',

  // Microsoft consumer billing / subscriptions / hardware
  'microsoft/how-to-cancel-microsoft-365-subscription',
  'microsoft/how-to-view-microsoft-purchase-history',
  'microsoft/how-to-check-surface-warranty-status',
  'microsoft/how-to-set-up-microsoft-mouse-keyboard-center',

  // Microsoft Windows / app maintenance (consumer searches)
  'microsoft/how-to-update-microsoft-outlook',
  'microsoft/how-to-update-microsoft-teams',
  'microsoft/how-to-uninstall-disable-microsoft-edge',
  'microsoft/how-to-disable-microsoft-compatibility-telemetry',
  'microsoft/how-to-use-microsoft-safety-scanner',

  // macOS personal troubleshooting
  'macos/how-to-erase-reinstall-macos-recovery',
  'macos/how-to-reset-smc-nvram-mac',

  // Generic "save as PDF" — high-volume consumer queries
  'google-workspace/how-to-save-google-doc-as-pdf',
  'microsoft-365/how-to-save-office-365-documents-as-pdf',

  // Microsoft 365 admin PowerShell — practitioner how-to, no conversion intent
  // (2026-06-24 ads expansion).
  'microsoft-365/how-to-install-and-connect-to-exchange-online-powershell',

  // CrowdStrike Falcon admin guides — EDR-admin / practitioner traffic doing
  // maintenance tasks, no services-conversion intent. Whole cluster monetized
  // via Auto Ads (2026-06-24 ads expansion).
  'crowdstrike/creating-and-managing-user-roles-in-crowdstrike-falcon',
  'crowdstrike/crowdstrik-exclusions-for-exchange',
  'crowdstrike/deploying-the-crowdstrike-falcon-sensor-using-group-policy-gpo',
  'crowdstrike/how-to-configure-crowdstrike-exclusions-for-hyper-v-hosts',
  'crowdstrike/how-to-configure-crowdstrike-exclusions-for-iis-internet-information-services',
  'crowdstrike/how-to-configure-crowdstrike-exclusions-for-sccm-configuration-manager',
  'crowdstrike/how-to-configure-crowdstrike-exclusions-for-sharepoint-server',
  'crowdstrike/how-to-configure-crowdstrike-exclusions-for-sql-server',
  'crowdstrike/how-to-configure-crowdstrike-falcon-proxy-linux',
  'crowdstrike/how-to-configure-crowdstrike-sensor-update-policies',
  'crowdstrike/how-to-configure-exclusions-in-crowdstrike-falcon',
  'crowdstrike/how-to-create-custom-indicators-of-compromise-iocs-in-crowdstrike-falcon',
  'crowdstrike/how-to-enable-uninstall-protection-crowdstrike-falcon-linux',
  'crowdstrike/how-to-install-crowdstrike-falcon-sensor-linux-secure-boot',
  'crowdstrike/how-to-install-crowdstrike-falcon-sensor-linux-with-installation-tokens',
  'crowdstrike/how-to-install-crowdstrike-falcon-sensor',
  'crowdstrike/how-to-install-the-crowdstrike-falcon-sensor-on-macos',
  'crowdstrike/how-to-install-the-crowdstrike-falcon-sensor-on-windows',
  'crowdstrike/how-to-integrate-crowdstrike-with-siem-solutions',
  'crowdstrike/how-to-investigate-a-security-incident-using-crowdstrike-falcon',
  'crowdstrike/how-to-manage-crowdstrike-falcon-grouping-tags-linux',
  'crowdstrike/how-to-manage-crowdstrike-sensor-versions',
  'crowdstrike/how-to-prepare-crowdstrike-falcon-master-image-linux',
  'crowdstrike/how-to-quarantine-and-contain-a-compromised-endpoint-in-crowdstrike-falcon',
  'crowdstrike/how-to-run-an-on-demand-scan-in-crowdstrike-falcon',
  'crowdstrike/how-to-schedule-crowdstrike-sensor-update-exclusions',
  'crowdstrike/how-to-setup-crowdstrike-device-control',
  'crowdstrike/how-to-setup-multi-factor-authentication-mfa-in-crowdstrike-falcon',
  'crowdstrike/how-to-setup-prevention-policies-in-crowdstrike-falcon',
  'crowdstrike/how-to-throttle-crowdstrike-sensor-updates',
  'crowdstrike/how-to-uninstall-crowdstrike-falcon-sensor',
  'crowdstrike/how-to-use-crowdstrike-bulk-maintenance-mode',
  'crowdstrike/how-to-use-falcon-x-for-automated-threat-intelligence',
  'crowdstrike/how-to-verify-the-crowdstrike-falcon-sensor-is-running-properly',
  'crowdstrike/implementing-crowdstrike-zero-trust-assessment-zta',

  // Sysadmin / IT-dev how-tos — task-driven, no conversion intent
  // (2026-06-24 ads expansion, batch 2).
  'microsoft/wsl-curl-command-not-found-fix',
  'microsoft-azure/azure-key-vault-tutorial',
]);

/**
 * KB categories enabled for ads wholesale (2026-06-24). Every article in these
 * categories renders Auto Ads EXCEPT articles claimed by a SaaS funnel
 * (Alert24 / GlitchReplay / WideAreaAI) — see isFunnelKBArticle, which keeps
 * those on their product CTA. This is what lets `checkpoint` and `devops` be
 * enabled wholesale even though each has a handful of Alert24 log-funnel pages.
 *
 * AI-dev categories (claude / copilot / gemini / openai) are intentionally
 * OMITTED — they are GlitchReplay/WideAreaAI funnel categories end-to-end.
 */
export const CONSUMER_KB_CATEGORIES = new Set<string>([
  '1password',
  'acronis',
  'aws',
  'checkpoint',
  'crowdstrike',
  'defender',
  'devops',
  'google-cloud',
  'google-workspace',
  'intune',
  'macos',
  'microsoft',
  'microsoft-365',
  'microsoft-azure',
  'proofpoint',
  'security-compliance',
  'sentinel',
]);

export const CONSUMER_BLOG_POSTS = new Set<string>([
  // Bucket-2 "ads" pages — high-traffic posts with no surviving SaaS-funnel fit
  // after the cyber-practice wind-down. Monetize via Auto Ads, not conversion
  // CTAs (2026-05-31 ads strategy; views = trailing 28-day GA4).
  'windows-update-commands-powershell-usoclient-amp-wuauclt', // ~2.9k/mo — sysadmin commands
  'azure-active-directory-microsoft-entra-id-guide',          // ~1.3k/mo — IT identity admin
  'trace-geographic-origin-email',                            // ~0.6k/mo — email forensics
  'biometric-authentication-far-frr-cer-guide',               // ~0.5k/mo — security concept

  // 2026-06-24 ads expansion — high-traffic sysadmin/dev how-tos, task-driven
  // visitors with no services-conversion intent.
  'how-to-install-curl-on-windows-complete-guide-with-5-methods', // sysadmin how-to
  'what-is-python-requirements-txt',                              // dev how-to

  // 2026-06-24 ads expansion (batch 2) — high-traffic sysadmin/dev/security
  // how-tos, task-driven visitors with no services-conversion intent.
  'hashicorp-vault-cli-install-and-gui-setup-guide',          // secrets mgmt how-to
  'ssl-certificate-formats-explained-pem-der-pfx-p7b',        // cert formats explainer
  'pyproject-toml-complete-guide',                            // python packaging
  // NOTE: discord-webhooks-guide removed — it is an Alert24 'incident' funnel
  // (alert24-funnel.ts). A page is a funnel page OR an ads page, never both.
  'why-dhcp-is-better-than-static-ip-addresses-even-for-servers', // networking concept
]);

export const CONSUMER_TOOLS = new Set<string>([
  // Bucket-2 "ads" pages — high-traffic utility/novelty tools with no SaaS-funnel
  // fit. Monetize via Auto Ads, not conversion CTAs (2026-05-31 ads strategy;
  // views = trailing 28-day GA4).
  'ascii-art-generator',      // ~3.2k/mo — novelty generator
  'xor-cipher',               // ~1.8k/mo — CTF / crypto toy
  'substitution-cipher',      // ~0.8k/mo — CTF / crypto toy
  'caesar-cipher',            // ~0.7k/mo — CTF / crypto toy
  'time-duration-calculator', // ~1.0k/mo — generic calculator

  // 2026-06-08 ads expansion — high-traffic utility tools, one-and-done visitors
  // with no services-conversion intent and no SaaS-funnel claim (views = trailing
  // 28-day GA4). Note: certificate-transparency-lookup and mtbf-mttr-calculator
  // were intentionally NOT added — they are Alert24 funnel targets (see
  // alert24-funnel.ts) and keep their conversion CTA instead of ads.
  'hash-lookup',                // ~12.8k/mo — hash identification utility
  'network-latency-calculator', // ~2.7k/mo — generic calculator
  'hex-editor',                 // ~2.0k/mo — utility
  'ip-risk-checker',            // ~1.2k/mo — lookup utility
  'machine-code-disassembler',  // ~1.1k/mo — CTF / RE utility
  'firewall-rule-simulator',    // ~0.9k/mo — utility
  'file-magic-number-checker',  // ~0.8k/mo — file-type utility
  'cve-lookup',                 // ~0.8k/mo — CVE lookup utility
  'cvss-calculator',            // ~0.8k/mo — scoring calculator
  'ssh-key-generator',          // ~0.8k/mo — key-gen utility

  // 2026-06-24 ads expansion (batch 2) — utility tools, no SaaS-funnel claim.
  'hash-generator',             // file-hash utility
  'string-extractor',           // RE / forensics utility
]);

/**
 * True if a KB article is claimed by any funnel (Alert24 / GlitchReplay /
 * WideAreaAI / software quote). Such pages keep their product CTA and must
 * never show ads, even when their category is ads-enabled wholesale via
 * CONSUMER_KB_CATEGORIES.
 *
 * The software-quote funnel is the largest carve-out by traffic: the vendor
 * categories it claims draw ~2,100 visits a month, worth roughly $90/yr as ad
 * impressions against an $18k contract if one reader in a few hundred asks for
 * a quote. See lib/data/software-quote-funnel.ts.
 */
function isFunnelKBArticle(category: string, slug: string): boolean {
  return (
    getAlert24VariantForKB(category, slug) !== undefined ||
    isGlitchReplayFunnelKB(category, slug) ||
    getWideAreaAIVariantForKB(category, slug) !== undefined ||
    isSoftwareQuoteFunnelKB(category, slug)
  );
}

export function isConsumerKBArticle(category: string, slug: string): boolean {
  // Invariant: a funnel page is never an ads page (one or the other, never both).
  if (isFunnelKBArticle(category, slug)) return false;

  // Ads-first: every KB article that no SaaS funnel has claimed monetizes via
  // ads. This used to be an allowlist (CONSUMER_KB_CATEGORIES /
  // CONSUMER_KB_ARTICLES), which left ~85% of KB traffic showing an
  // IT-services conversion CTA instead of an ad — a funnel we no longer sell
  // into. The allowlists are kept below only as a record of what shipped first;
  // they no longer gate anything.
  //
  // To carve out an exception, claim the page in a funnel (see *-funnel.ts).
  return true;
}

export function isConsumerBlogPost(slug: string): boolean {
  // Invariant: a funnel page is never an ads page. WideAreaAI was missing from
  // this guard — harmless while the allowlist below gated everything, but it
  // would have leaked ads onto WideAreaAI funnel posts once the gate came off.
  if (
    getAlert24VariantForBlog(slug) ||
    getPlanetRoadmapVariantForBlog(slug) ||
    getWideAreaAIVariantForBlog(slug)
  ) {
    return false;
  }

  // Ads-first, matching isConsumerKBArticle: every post no funnel has claimed
  // monetizes via ads. The allowlist held 11 of 1,089 posts, so ~70% of blog
  // clicks landed on pages serving neither an ad nor a funnel we still sell
  // into. CONSUMER_BLOG_POSTS is kept below as a record of what shipped first;
  // it no longer gates anything. To carve out an exception, claim the post in
  // a funnel (see *-funnel.ts).
  return true;
}

export function isConsumerTool(slug: string): boolean {
  // Ads-first, matching isConsumerKBArticle and isConsumerBlogPost. The
  // allowlist held 17 of 294 tools, leaving ~5,200 tool clicks a quarter on
  // pages serving no ad at all. No funnel currently claims a tool, so there is
  // nothing to guard against here — if one ever does, add the check above the
  // return exactly as the blog and KB variants do, or those pages will show
  // both a funnel CTA and an ad.
  return true;
}
